import FormField from "./FormField";
import { useRef } from "react";
import Button from "../../../components/Button/Button";

const ProfileInfoCard = ({
  admin,
  isEditing,
  formData,
  setIsEditing,
  setFormData,
  handleChange,
  handleSave,
  profileImage,
  onImageUpload,
  onImageDelete,
}) => {
  const fileInputRef = useRef(null);

  const handleImageClick = () => {
    if (isEditing) fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) onImageUpload(file);
    e.target.value = "";
  };

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 pb-5">
        <div className="flex items-center gap-3.5">

          {/* Profile Image */}
          <div className="relative group">
            <img
              src={profileImage || `https://ui-avatars.com/api/?name=${admin.first_name}+${admin.last_name}&background=random`}
              alt="Profile"
              className={`h-14 w-14 rounded-full object-cover ${isEditing ? "cursor-pointer" : ""}`}
              onClick={handleImageClick}
            />

            {/* Hover overlay - only in edit mode */}
            {isEditing && (
              <div
                onClick={handleImageClick}
                className="absolute inset-0 bg-black bg-opacity-40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition cursor-pointer"
              >
                <span className="text-white text-xs font-medium">Change</span>
              </div>
            )}

            {/* Delete button - only in edit mode and if a real image exists */}
            {isEditing && profileImage && (
              <button
                onClick={onImageDelete}
                title="Remove photo"
                className="absolute -top-1 -right-1 bg-danger-500 hover:bg-danger-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs leading-none"
              >
                ✕
              </button>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/webp"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-tight text-gray-900">
              {admin.first_name} {admin.last_name}
            </h2>
            <p className="text-xs text-gray-500">{admin.email}</p>
          </div>
        </div>

        {!isEditing ? (
          <Button size="sm" onClick={() => setIsEditing(true)}>Edit</Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave}>Save</Button>
            <Button size="sm"
              variant="secondary"
              onClick={() => {
                setIsEditing(false);
                setFormData(admin);
              }}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Personal Information */}
      <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
        Personal Information
      </h3>
      <div className="grid grid-cols-1 items-start gap-x-5 gap-y-4 md:grid-cols-2">
        <FormField fieldKey="first_name" label="First Name" required {...{ isEditing, formData, admin, handleChange }} />
        <FormField fieldKey="last_name" label="Last Name" required {...{ isEditing, formData, admin, handleChange }} />
        <FormField fieldKey="gender" label="Gender" type="select" {...{ isEditing, formData, admin, handleChange }} />
        <FormField fieldKey="linkedin_url" label="LinkedIn Profile" type="url" {...{ isEditing, formData, admin, handleChange }} />
        <FormField fieldKey="phone" label="Mobile Phone" {...{ isEditing, formData, admin, handleChange }} />
        <FormField fieldKey="email" label="Email" required readOnly {...{ isEditing, formData, admin, handleChange }} />
        <FormField fieldKey="teams_id" label="Microsoft Teams ID" {...{ isEditing, formData, admin, handleChange }} />
        <FormField fieldKey="zoom_id" label="Zoom ID" {...{ isEditing, formData, admin, handleChange }} />
        <FormField fieldKey="country" label="Country" type="select" required {...{ isEditing, formData, admin, handleChange }} />
        <FormField fieldKey="language_email" label="Language for Emails" type="select" required {...{ isEditing, formData, admin, handleChange }} />
        <FormField fieldKey="time_zone" label="Time Zone" type="select" required {...{ isEditing, formData, admin, handleChange }} />
        {admin.default_currency !== undefined && (
          <FormField fieldKey="default_currency" label="Default Currency" readOnly {...{ isEditing, formData, admin, handleChange }} />
        )}
        <FormField fieldKey="createdAt" label="Registered At" readOnly {...{ isEditing, formData, admin, handleChange }} />
      </div>

      {isEditing && (
        <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500">
          Fields marked with <span className="text-danger-500">*</span> are mandatory.
        </p>
      )}
    </div>
  );
};

export default ProfileInfoCard;